// Cuisine strategy types
export type CuisineStrategy =
  | 'favorites-only'  // Only use user's selected favorite cuisines (2-4 required)
  | 'mixed'           // Mix of user's favorites (1-2) + AI variety
  | 'surprise'        // AI picks diverse cuisines (respects dietary restrictions)

// Persistent preferences - saved to profile, required on first setup
export interface PersistentPreferences {
  dietaryRestrictions: string[] // Custom restrictions (e.g., "no red meat", "low sodium")
  primaryDietaryPreference: DietaryPreference | null // Primary diet type
  kitchenAppliances: string[] // Kitchen equipment available
  unitsSystem: UnitsSystem | null // Metric vs Imperial measurements
  measurementBias: MeasurementBias | null // Volume vs Weight preference
  cuisineStrategy: CuisineStrategy // How cuisines are chosen
  favoriteCuisines: string[] // User's favorite cuisines (used for favorites-only and mixed modes)
  calorieTargeting: {
    enabled: boolean
    perMealTargets: CalorieTargets
  }
}

export type DietaryPreference = 
  | 'omnivore'
  | 'keto'
  | 'vegan' 
  | 'vegetarian'
  | 'paleo'
  | 'mediterranean'
  | 'high_protein'
  | 'low_carb'
  | 'gluten_free'
  | 'dairy_free'

export type MealTimingPreference = 
  | 'quick'     // Under 30 minutes
  | 'standard'  // 30-60 minutes  
  | 'elaborate' // 60+ minutes, elaborate preparation

export type UnitsSystem = 
  | 'metric'    // Metric system (grams, liters, celsius)
  | 'imperial'  // Imperial system (ounces, cups, fahrenheit)

export type MeasurementBias = 
  | 'volume'    // Prefer volumetric measurements (cups, tablespoons)
  | 'weight'    // Prefer weight measurements (grams, ounces)

// Session preferences - optional to remember, asked each planning session
export interface SessionPreferences {
  numberOfPeople: number // How many people this meal plan serves
  mealTimingPreference: MealTimingPreference | null // How long user wants to spend cooking
  mealTypes: MealTypeSelection[] // Which meals to plan (breakfast, lunch, dinner, snacks)
  mealCounts: MealCounts // How many of each meal type
  calorieTargeting: {
    enabled: boolean // Whether to use calorie targets per meal
    perMealTargets: CalorieTargets // Calorie targets per meal type
  }
  cuisinePreferences: string[] // Selected cuisine types (optional)
  rememberSettings: boolean // User choice to save these for next time
}

export interface MealTypeSelection {
  type: 'breakfast' | 'lunch' | 'dinner' | 'snacks' | 'dessert'
  enabled: boolean
}

export interface MealCounts {
  breakfast: number
  lunch: number
  dinner: number
  snacks: number
  dessert: number
}

export interface CalorieTargets {
  breakfast: number
  lunch: number
  dinner: number
  snacks: number
  dessert: number
}

// Complete user preferences combining both types
export interface UserPreferences {
  // Persistent preferences (always saved)
  persistent: PersistentPreferences
  
  // Session preferences (optionally saved)
  session: SessionPreferences | null // null if user chooses not to remember
  
  // Metadata
  setup_completed: boolean
  last_updated: string
}

// Weekly defaults for "Plan Your Week" entry point — always 5-day workweek regardless of saved prefs
export const WORKWEEK_MEAL_COUNTS: MealCounts = {
  breakfast: 5,
  lunch: 5,
  dinner: 5,
  snacks: 0,
  dessert: 0,
}

// Default values
export const DEFAULT_PERSISTENT_PREFERENCES: PersistentPreferences = {
  dietaryRestrictions: [],
  primaryDietaryPreference: null,
  kitchenAppliances: ['oven', 'stovetop', 'microwave'], // Most common defaults
  unitsSystem: null,
  measurementBias: null,
  cuisineStrategy: 'surprise',
  favoriteCuisines: [],
  calorieTargeting: {
    enabled: false,
    perMealTargets: {
      breakfast: 400,
      lunch: 500,
      dinner: 700,
      snacks: 150,
      dessert: 300,
    }
  }
}

export const DEFAULT_SESSION_PREFERENCES: SessionPreferences = {
  numberOfPeople: 2,
  mealTimingPreference: null,
  mealTypes: [
    { type: 'breakfast', enabled: true } as MealTypeSelection,
    { type: 'lunch', enabled: true } as MealTypeSelection,
    { type: 'dinner', enabled: true } as MealTypeSelection,
    { type: 'snacks', enabled: false } as MealTypeSelection,
    { type: 'dessert', enabled: false } as MealTypeSelection,
  ],
  mealCounts: {
    breakfast: 7,
    lunch: 7,
    dinner: 7,
    snacks: 0,
    dessert: 0,
  },
  calorieTargeting: {
    enabled: false,
    perMealTargets: {
      breakfast: 400,
      lunch: 500,
      dinner: 600,
      snacks: 200,
      dessert: 300,
    }
  },
  cuisinePreferences: [],
  rememberSettings: false,
}

// Dietary preference templates with descriptions
export const DIETARY_PREFERENCES: { value: DietaryPreference; label: string; description: string }[] = [
  { value: 'omnivore', label: 'Omnivore', description: 'Eat everything - no dietary restrictions' },
  { value: 'keto', label: 'Keto', description: 'Low-carb, high-fat diet' },
  { value: 'vegan', label: 'Vegan', description: 'Plant-based diet, no animal products' },
  { value: 'vegetarian', label: 'Vegetarian', description: 'No meat or fish' },
  { value: 'paleo', label: 'Paleo', description: 'Whole foods, no processed foods' },
  { value: 'mediterranean', label: 'Mediterranean', description: 'Heart-healthy Mediterranean diet' },
  { value: 'high_protein', label: 'High Protein', description: 'Protein-focused for fitness goals' },
  { value: 'low_carb', label: 'Low Carb', description: 'Reduced carbohydrate intake' },
  { value: 'gluten_free', label: 'Gluten Free', description: 'No gluten-containing foods' },
  { value: 'dairy_free', label: 'Dairy Free', description: 'No dairy products' },
]

// Meal timing preference templates
export const MEAL_TIMING_PREFERENCES: { value: MealTimingPreference; label: string; description: string }[] = [
  { value: 'quick', label: 'Quick Meals', description: 'Under 30 minutes - fast and simple' },
  { value: 'standard', label: 'Standard Meals', description: '30-60 minutes - balanced preparation' },
  { value: 'elaborate', label: 'Elaborate Meals', description: '60+ minutes - detailed and special' },
]

// Units system preference templates
export const UNITS_SYSTEM_PREFERENCES: { value: UnitsSystem; label: string; description: string }[] = [
  { value: 'metric', label: 'Metric', description: 'Grams, liters, Celsius (kg, ml, °C)' },
  { value: 'imperial', label: 'Imperial', description: 'Ounces, cups, Fahrenheit (lbs, fl oz, °F)' },
]

// Measurement bias preference templates
export const MEASUREMENT_BIAS_PREFERENCES: { value: MeasurementBias; label: string; description: string }[] = [
  { value: 'volume', label: 'Volume-Based', description: 'Prefer cups, tablespoons, teaspoons' },
  { value: 'weight', label: 'Weight-Based', description: 'Prefer grams, ounces for precision' },
]

// Available appliances with labels and emojis
export const AVAILABLE_APPLIANCES = [
  { value: 'oven', label: 'Oven', emoji: '🔥' },
  { value: 'stovetop', label: 'Stovetop', emoji: '🍳' },
  { value: 'microwave', label: 'Microwave', emoji: '📡' },
  { value: 'air_fryer', label: 'Air Fryer', emoji: '💨' },
  { value: 'slow_cooker', label: 'Slow Cooker', emoji: '🍲' },
  { value: 'instant_pot', label: 'Instant Pot', emoji: '⚡' },
  { value: 'grill', label: 'Grill', emoji: '🔥' },
  { value: 'blender', label: 'Blender', emoji: '🌀' },
  { value: 'food_processor', label: 'Food Processor', emoji: '⚙️' },
  { value: 'stand_mixer', label: 'Stand Mixer', emoji: '🥄' },
  { value: 'toaster', label: 'Toaster', emoji: '🍞' },
  { value: 'rice_cooker', label: 'Rice Cooker', emoji: '🍚' },
] as const

// Common dietary restrictions
export const COMMON_RESTRICTIONS = [
  'No red meat',
  'No pork',
  'No shellfish',
  'Low sodium',
  'Low sugar',
  'No nuts',
  'No seeds',
  'No Seafood',      // NEW: Added per your feedback
  'Not Spicy',       // NEW: Added per your feedback
  'Halal',
  'Kosher',
  'No alcohol in cooking',
]

// Available cuisine types
export const CUISINE_TYPES = [
  { value: 'american', label: 'American', emoji: '🇺🇸' },
  { value: 'italian', label: 'Italian', emoji: '🇮🇹' },
  { value: 'mexican', label: 'Mexican', emoji: '🇲🇽' },
  { value: 'chinese', label: 'Chinese', emoji: '🇨🇳' },
  { value: 'japanese', label: 'Japanese', emoji: '🇯🇵' },
  { value: 'indian', label: 'Indian', emoji: '🇮🇳' },
  { value: 'thai', label: 'Thai', emoji: '🇹🇭' },
  { value: 'french', label: 'French', emoji: '🇫🇷' },
  { value: 'greek', label: 'Greek', emoji: '🇬🇷' },
  { value: 'mediterranean', label: 'Mediterranean', emoji: '🫒' },
  { value: 'middle_eastern', label: 'Middle Eastern', emoji: '🧆' },
  { value: 'korean', label: 'Korean', emoji: '🇰🇷' },
  { value: 'spanish', label: 'Spanish', emoji: '🇪🇸' },
  { value: 'vietnamese', label: 'Vietnamese', emoji: '🇻🇳' },
  { value: 'moroccan', label: 'Moroccan', emoji: '🇲🇦' },
  { value: 'lebanese', label: 'Lebanese', emoji: '🇱🇧' },
  { value: 'cajun', label: 'Cajun/Creole', emoji: '🎺' },
  { value: 'brazilian', label: 'Brazilian', emoji: '🇧🇷' },
]

/**
 * Convert user preferences to RecipeDetailRequest format with sensible defaults.
 * Handles nullable preference fields by providing defaults suitable for most users.
 *
 * @param persistent - User's persistent preferences (dietary, equipment, units)
 * @param session - User's session preferences (timing, meal types)
 * @returns Formatted preferences for AI recipe generation with all required fields
 */
export function getRecipeDetailPreferences(
  persistent: PersistentPreferences,
  session: SessionPreferences
): {
  dietaryRestrictions: string[]
  dietaryPreference: string
  availableAppliances: string[]
  mealTimingPreference: 'quick' | 'standard' | 'elaborate'
  unitsSystem: 'metric' | 'imperial'
  measurementBias: 'volume' | 'weight'
} {
  return {
    dietaryRestrictions: persistent.dietaryRestrictions,
    dietaryPreference: persistent.primaryDietaryPreference ?? 'omnivore',
    availableAppliances: persistent.kitchenAppliances,
    mealTimingPreference: session.mealTimingPreference ?? 'standard',
    unitsSystem: persistent.unitsSystem ?? 'imperial',
    measurementBias: persistent.measurementBias ?? 'volume',
  }
}